import { computed, readonly, ref, shallowRef } from "vue";
import { deleteMemberAddresses, getMyAddresses, updateMemberAddresses } from "@/core/api/graphql/account";
import { SortDirection } from "@/core/enums";
import { getSortingExpression, isEqualAddresses, Logger, toInputAddress } from "@/core/utilities";
import { useUser } from "./useUser";
import type { InputMemberAddressType, MemberAddressFieldsFragment } from "@/core/api/graphql/types";
import type { AnyAddressType, ISortInfo } from "@/core/types";

const loading = ref(false);
const addresses = shallowRef<MemberAddressFieldsFragment[]>([]);
const sort = ref<ISortInfo>({
  column: "lastName",
  direction: SortDirection.Ascending,
});

export function useUserAddresses() {
  const { user } = useUser();

  function isExistAddress(address: AnyAddressType): boolean {
    return addresses.value.some((item) => isEqualAddresses(item, address));
  }

  async function fetchAddresses() {
    Logger.info("useUserAddresses", "[FETCH] Starting fetchAddresses");
    loading.value = true;

    const sortingExpression = getSortingExpression(sort.value);

    try {
      addresses.value = await getMyAddresses({ sort: sortingExpression });
      Logger.info("useUserAddresses", "[FETCH] Fetched addresses:", {
        count: addresses.value.length,
        ids: addresses.value.map((a) => a.id),
      });
    } catch (e) {
      Logger.error(`${useUserAddresses.name}.${fetchAddresses.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function updateAddresses(items: MemberAddressFieldsFragment[]): Promise<void> {
    Logger.info("useUserAddresses", "[UPDATE] Starting updateAddresses with", {
      count: items.length,
      ids: items.map((a) => a.id),
    });
    if (!user.value.memberId) {
      Logger.warn("useUserAddresses", "[UPDATE] No memberId, returning early");
      return;
    }

    loading.value = true;

    const inputAddresses: InputMemberAddressType[] = items.map(toInputAddress);

    try {
      Logger.info("useUserAddresses", "[UPDATE] Calling updateMemberAddresses API");
      await updateMemberAddresses(user.value.memberId, inputAddresses);
      Logger.info("useUserAddresses", "[UPDATE] API call successful");
    } catch (e) {
      Logger.error(`${useUserAddresses.name}.${updateAddresses.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }

    Logger.info("useUserAddresses", "[UPDATE] About to call fetchAddresses");
    await fetchAddresses();
    Logger.info("useUserAddresses", "[UPDATE] Completed fetchAddresses, addresses now:", {
      count: addresses.value.length,
      ids: addresses.value.map((a) => a.id),
    });
  }

  async function addOrUpdateAddresses(items: MemberAddressFieldsFragment[]): Promise<void> {
    Logger.info("useUserAddresses", "[ADD_OR_UPDATE] Starting", {
      itemsCount: items.length,
      itemIds: items.map((i) => i.id),
    });
    if (!items.length) {
      return;
    }

    loading.value = true;

    const updatedAddresses: MemberAddressFieldsFragment[] = addresses.value.slice();
    Logger.info("useUserAddresses", "[ADD_OR_UPDATE] Existing addresses BEFORE:", {
      count: addresses.value.length,
      ids: addresses.value.map((a) => a.id),
    });

    items.forEach((newAddress: MemberAddressFieldsFragment) => {
      const index = updatedAddresses.findIndex((oldAddress) => oldAddress.id === newAddress.id);
      Logger.info("useUserAddresses", "[ADD_OR_UPDATE] Processing item:", {
        newAddressId: newAddress.id,
        foundAtIndex: index,
      });

      if (index === -1) {
        updatedAddresses.push(newAddress);
        Logger.info("useUserAddresses", "[ADD_OR_UPDATE] Pushed new address (no id match)");
      } else {
        updatedAddresses.splice(index, 1, newAddress);
        Logger.info("useUserAddresses", "[ADD_OR_UPDATE] Replaced existing address at index", index);
      }
    });

    Logger.info("useUserAddresses", "[ADD_OR_UPDATE] Calling updateAddresses with", {
      count: updatedAddresses.length,
      ids: updatedAddresses.map((a) => a.id),
    });
    await updateAddresses(updatedAddresses);
    Logger.info("useUserAddresses", "[ADD_OR_UPDATE] AFTER updateAddresses, addresses.value:", {
      count: addresses.value.length,
      ids: addresses.value.map((a) => a.id),
    });
  }

  async function removeAddresses(items: MemberAddressFieldsFragment[]): Promise<void> {
    if (!items.length) {
      return;
    }

    loading.value = true;

    const inputAddresses: InputMemberAddressType[] = items.map(toInputAddress);

    try {
      await deleteMemberAddresses(inputAddresses, user.value.memberId!);
    } catch (e) {
      Logger.error(`${useUserAddresses.name}.${removeAddresses.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }

    await fetchAddresses();
  }

  return {
    sort,
    isExistAddress,
    fetchAddresses,
    updateAddresses,
    addOrUpdateAddresses,
    removeAddresses,
    loading: readonly(loading),
    addresses: computed(() => addresses.value),
  };
}
